import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import config from '../utils/config';
import { 
  fetchAuthGuardProfile, 
  hasAdminPermission, 
  AuthGuardProfile 
} from '../services/authGuardProfileService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthGuardUser | null;
  login: (redirectTo?: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  getAccessToken: () => Promise<string | null>;
  isAdmin: boolean;
  userProfile: AuthGuardProfile | null;
}

// Auth settings are now handled by the backend proxy
// No more hardcoded client secrets in frontend!

interface AuthGuardJwtPayload {
  application: string;
  profile_id: string;
  profile_name: string;
  resource_id: string;
  user_display_name: string;
  email: string;
  user_entra_id: string;
  impersonator_user_entra_id?: string;
  is_impersonated?: string;
  iss?: string;
  aud?: string;
  nbf?: number;
  exp?: number;
  iat?: number;
}

interface AuthGuardUser {
  displayName: string;
  email: string;
  profileId: string;
  resourceId: string;
  isImpersonated: boolean;
  rawClaims: AuthGuardJwtPayload;
}

const AUTH_TOKEN_KEY = 'authguard_access_token';
const AUTH_USER_KEY = 'authguard_user';
const LOGIN_REDIRECT_AFTER_KEY = 'authguard_redirect_after_login';
const LOGOUT_SUPPRESS_LOGIN_KEY = 'authguard_logout_suppress_login';

// Helper function to get the backend API base URL
const getBackendApiBase = () => {
  // Use the existing config system to get the snowflake module API URL
  // The auth proxy is implemented in the module-snowflake backend
  return config.api.snowflake;
};

const parseJwtPayload = (token: string): AuthGuardJwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to parse JWT payload:', e);
      return null;
    }
  };

const userFromClaims = (claims: AuthGuardJwtPayload): AuthGuardUser => {
  return {
    displayName: claims.user_display_name || claims.profile_name || claims.email,
    email: claims.email,
    profileId: claims.profile_id,
    resourceId: claims.resource_id,
    isImpersonated: String(claims.is_impersonated).toLowerCase() === 'true',
    rawClaims: claims,
  };
};

export const isTokenExpired = (token: string, skewSeconds: number = 30): boolean => {
  const claims = parseJwtPayload(token);
  if (!claims?.exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp <= nowSec + skewSeconds;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthGuardUser | null>(null);
  const [userProfile, setUserProfile] = useState<AuthGuardProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Initialize from storage on load
    const initAuth = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = localStorage.getItem(AUTH_USER_KEY);
      
      if (token) {
        if (isTokenExpired(token)) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_USER_KEY);
          setLoading(false);
        } else {
          setAccessToken(token);
          let currentUser: AuthGuardUser | null = null;
          
          if (storedUser) {
            try {
              currentUser = JSON.parse(storedUser);
              setUser(currentUser);
            } catch {
              localStorage.removeItem(AUTH_USER_KEY);
            }
          } else {
            const claims = parseJwtPayload(token);
            if (claims) {
              currentUser = userFromClaims(claims);
              setUser(currentUser);
              localStorage.setItem(AUTH_USER_KEY, JSON.stringify(currentUser));
            }
          }
          
          // Fetch profile after user is set
          if (currentUser) {
            try {
              const profile = await fetchAuthGuardProfile(currentUser.profileId, token);
              setUserProfile(profile);
              setIsAdmin(hasAdminPermission(profile));
            } catch (error) {
              console.error('Failed to fetch AuthGuard profile:', error);
              setUserProfile(null);
              setIsAdmin(false);
            }
          }
          
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);
  
  const getAccessToken = async (): Promise<string | null> => {
    if (accessToken && !isTokenExpired(accessToken)) {
      return accessToken;
    }
    // Expired: clear and force re-login on next guard
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    return null;
  };

  const login = async (redirectTo?: string): Promise<boolean> => {
    try {
      // Clear any logout suppression when starting a fresh login
      sessionStorage.removeItem(LOGOUT_SUPPRESS_LOGIN_KEY);
      if (redirectTo) {
        localStorage.setItem(LOGIN_REDIRECT_AFTER_KEY, redirectTo);
      } else {
        // Store current location to return after login
        const current = window.location.pathname + window.location.search + window.location.hash;
        localStorage.setItem(LOGIN_REDIRECT_AFTER_KEY, current);
      }
      
      // Use backend proxy for authentication
      const backendBase = getBackendApiBase();
      const params = new URLSearchParams();
      const redirectUri = new URL('/authguard_redirect', window.location.origin).toString();
      params.set('redirect_to', redirectUri);
      const loginUrl = `${backendBase}/api/v1/auth/authorize${params.toString() ? '?' + params.toString() : ''}`;
      
      window.location.href = loginUrl;
      return true;
    } catch (e) {
      console.error('AuthGuard login redirect failed:', e);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Prevent AuthGuard from triggering login during the logout navigation
      sessionStorage.setItem(LOGOUT_SUPPRESS_LOGIN_KEY, '1');

      // Clear stored credentials
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(LOGIN_REDIRECT_AFTER_KEY);
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(LOGIN_REDIRECT_AFTER_KEY);

      // Get logout URL from backend proxy
      const backendBase = getBackendApiBase();
      const response = await fetch(`${backendBase}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Perform a hard navigation to sign-out (replace to avoid back navigation to authenticated state)
        window.location.replace(data.logout_url);
      } else {
        console.error('Failed to get logout URL from backend');
        // Fallback: just redirect to home page
        window.location.replace('/');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: just redirect to home page
      window.location.replace('/');
    }
  };

  const contextValue = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: !!accessToken && !!user,
        user,
        login,
        logout,
        loading,
        getAccessToken,
        isAdmin,
        userProfile,
    }),
    [accessToken, user, loading, isAdmin, userProfile]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// Helper to complete login from the redirect handler (with optional backend validation)
export const completeAuthGuardLogin = async (token: string, useBackendValidation = false) => {
  if (useBackendValidation) {
    // Use backend for token validation
    try {
      const backendBase = config.api.snowflake;
      const response = await fetch(`${backendBase}/api/v1/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: token }),
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
        return;
      } else {
        console.error('Backend token validation failed');
        throw new Error('Token validation failed');
      }
    } catch (error) {
      console.error('Error validating token with backend:', error);
      // Fall back to client-side validation
    }
  }
  
  // Client-side token validation (original method)
  const claims = parseJwtPayload(token);
  if (!claims) throw new Error('Invalid token');
  const derivedUser = userFromClaims(claims);
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(derivedUser));
};

export default AuthProvider;
